/**
 * DashboardMenu — iter86.95 One UI Phase 1. The floating glass menu.
 *
 * ONE component, responsive anchoring only: a FAB (bottom-right,
 * auto-hides on scroll down) triggers it on mobile; a top-corner "Menu"
 * pill in the HeroStrip triggers it on desktop. Both open the same
 * GlassSheet (bottom sheet on mobile / floating panel on desktop).
 *
 * Absorbs the retired DashboardHeader clutter + HeaderMoreMenu + the
 * dashboard's AdminMenu dropdown: identity, manual, crew/standings,
 * rules, recap, invite copy, admin entries, sign-out.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
    Menu, GraduationCap, Users, LogOut,
    Sparkles, Trophy, Settings, Mail, MessageCircleWarning, Crown,
} from "lucide-react";
import GlassSheet from "@/components/onui/GlassSheet";
import ReportIssueModal from "@/components/ReportIssueModal";
import { hasSeenPro } from "@/pro/session";

const Row = ({ icon: Icon, emoji, label, sub, onClick, to, testId, danger, trailing, title }) => {
    const cls = `w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:scale-[0.98] ${danger ? "text-rose-400 hover:bg-rose-500/10" : "hover:bg-white/[0.06]"}`;
    const body = (
        <>
            <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${danger ? "bg-rose-500/10" : "bg-white/[0.06]"}`}>
                {emoji ? <span className="text-lg leading-none">{emoji}</span> : Icon && <Icon className="w-4 h-4" />}
            </span>
            <span className="flex-1 min-w-0">
                <span className="block font-display font-extrabold text-sm uppercase tracking-tight truncate">{label}</span>
                {sub && <span className="block text-[11px] text-zinc-400 truncate">{sub}</span>}
            </span>
            {trailing}
        </>
    );
    return to ? (
        <Link to={to} data-testid={testId} onClick={onClick} title={title} className={cls}>{body}</Link>
    ) : (
        <button type="button" data-testid={testId} onClick={onClick} title={title} className={cls}>{body}</button>
    );
};

const SectionLabel = ({ children }) => (
    <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500">{children}</p>
);

export default function DashboardMenu({
    leagueId,
    league,
    tournament,
    tournamentCount,
    me,
    myAvatar,
    myFlair,
    isSpectator,
    seasonStanding,
    onOpenUserGuide,
    onOpenTinyCrew,
    onOpenCrewIdentity,
    onOpenAccount,
    onOpenScoring,
    onCopyInvite,
    copied,
    onLogout,
    onOpenSetupWizard,
}) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [fabHidden, setFabHidden] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    // iter87.199.28 — "Return to Pro+" shows when EITHER the legacy
    // Pro+ session is on this device OR the Pro+ shell has verified
    // this device before (Clerk-authed users). Read once at mount.
    const [hasProSession] = useState(() => hasSeenPro());

    // FAB auto-hides on scroll down, returns on scroll up.
    useEffect(() => {
        let last = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setFabHidden(y > last && y > 80);
            last = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (isSpectator) return null;

    const isAdmin = !!(me?.is_admin || me?.is_co_admin);
    const closeAfter = (fn) => () => { setOpen(false); fn?.(); };
    const pickAdmin = (scope) => {
        setOpen(false);
        const params = new URLSearchParams({ scope });
        if (tournament?.id) params.set("tournament_id", tournament.id);
        navigate(`/league/${leagueId}/admin?${params.toString()}`);
    };

    return (
        <>
            {/* Desktop trigger — top corner of the HeroStrip. */}
            <button
                type="button"
                data-testid="dashboard-menu-trigger"
                onClick={() => setOpen(true)}
                className="hidden lg:inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-4 min-h-[40px] text-xs font-display font-extrabold uppercase tracking-wide transition-colors active:scale-95"
            >
                <Menu className="w-4 h-4" /> Menu
            </button>

            {/* Mobile FAB — bottom-right, auto-hides on scroll down.
                Portaled to <body>: the glass HeroStrip's backdrop-filter
                would otherwise become this fixed button's containing block. */}
            {createPortal(
                <button
                    type="button"
                    data-testid="dashboard-menu-fab"
                    onClick={() => setOpen(true)}
                    aria-label="Open menu"
                    className={`lg:hidden fixed bottom-5 right-4 z-40 w-14 h-14 rounded-full bg-cyber text-black border-2 border-black shadow-[0_10px_30px_rgba(255,204,0,0.35)] flex items-center justify-center transition-all duration-300 active:scale-90 ${fabHidden && !open ? "translate-y-24 opacity-0 pointer-events-none" : ""}`}
                >
                    <Menu className="w-6 h-6" strokeWidth={2.5} />
                </button>,
                document.body
            )}

            <GlassSheet open={open} onClose={() => setOpen(false)} title="Menu" testId="dashboard-menu-sheet">
                <div className="space-y-0.5">
                    <SectionLabel>You</SectionLabel>
                    {me?.id && onOpenCrewIdentity && (
                        <Row
                            emoji={myAvatar || "🙂"}
                            label={myAvatar || myFlair ? "Crew Identity" : "Set your identity"}
                            sub={myFlair || "Avatar + flair for the leaderboard"}
                            onClick={closeAfter(onOpenCrewIdentity)}
                            testId="dashboard-crew-identity-btn"
                        />
                    )}
                    {/* iter86.99 — Account: self-service recovery email
                        (username read-only — admin renames only). */}
                    {me?.id && onOpenAccount && (
                        <Row
                            icon={Mail}
                            label="Account"
                            sub={me?.email || "Add a recovery email"}
                            onClick={closeAfter(onOpenAccount)}
                            testId="dashboard-account-btn"
                        />
                    )}
                    <Row
                        icon={GraduationCap}
                        label="Your Rivalry Manual"
                        sub="How this game works"
                        onClick={closeAfter(onOpenUserGuide)}
                        testId="dashboard-user-guide-btn"
                    />
                    {/* iter87.199.5 — Crown Race (formerly Rivalry Points)
                        deprecated from the player-facing menu per operator.
                        Backend tally keeps humming silently (finalize
                        logic, /crown-points endpoint, aggregation) so we
                        can flip it back on later without a migration.
                        `Your Tiny Crew` becomes the always-on Row. */}
                    {onOpenTinyCrew && (
                        <Row
                            icon={Users}
                            label="Your Tiny Crew"
                            sub="Everyone in this Rivalry"
                            onClick={closeAfter(onOpenTinyCrew)}
                            testId="dashboard-tiny-crew-btn"
                        />
                    )}

                    {/* iter87.1 — Removed empty "Rivalry" section label.
                        Scoring & Rules moved to the Intel plane; Recap is
                        now a home tile; invite/personal-code copy lives in
                        the Account sheet — nothing left under the label,
                        so the label itself is retired. */}

                    {isAdmin && (
                        <>
                            <SectionLabel>Admin</SectionLabel>
                            <Row
                                icon={Trophy}
                                label="Tournament Admin"
                                sub="Teams, schedule, scoring, draft"
                                onClick={() => pickAdmin("tournament")}
                                testId="admin-menu-tournament-btn"
                            />
                            <Row
                                icon={Settings}
                                label="Rivalry Admin"
                                sub="Members, invites, transfer admin"
                                onClick={() => pickAdmin("league")}
                                testId="admin-menu-league-btn"
                            />
                            {onOpenSetupWizard && (
                                <Row
                                    icon={Sparkles}
                                    label="Show setup wizard"
                                    sub="Re-open the guided onboarding"
                                    onClick={closeAfter(onOpenSetupWizard)}
                                    testId="admin-menu-setup-wizard-btn"
                                />
                            )}
                            {/* iter87.105.1 — Report an Issue promoted to
                                top-level admin drawer (was buried three
                                clicks deep inside Rivalry Admin → Settings). */}
                            <Row
                                icon={MessageCircleWarning}
                                label="Report an issue"
                                sub="Bug, confusion, or feature idea"
                                onClick={() => { setOpen(false); setReportOpen(true); }}
                                testId="admin-menu-report-issue-btn"
                            />
                        </>
                    )}

                    <div className="border-t border-white/10 my-2" />
                    {hasProSession ? (
                        <Row
                            icon={Crown}
                            label="Return to Pro+"
                            sub="Back to your Pro+ console"
                            to="/rivalry-pro"
                            onClick={() => setOpen(false)}
                            testId="return-to-pro-plus-btn"
                        />
                    ) : isAdmin && (
                        // iter87.199.159 — Free admins get the self-serve
                        // upgrade path here too, mirroring the Pro+ "Return
                        // to Pro+" row above (mutually exclusive).
                        <Row
                            icon={Crown}
                            label="Sign up for Pro+"
                            sub="Unlimited tournaments, AI recaps & more"
                            to="/rivalry-pro"
                            onClick={() => setOpen(false)}
                            testId="signup-for-pro-plus-btn"
                        />
                    )}
                    <Row
                        icon={LogOut}
                        label="Sign out"
                        onClick={closeAfter(onLogout)}
                        testId="logout-btn"
                        danger
                    />
                </div>
            </GlassSheet>
            <ReportIssueModal
                open={reportOpen}
                onClose={() => setReportOpen(false)}
                leagueId={leagueId}
            />
        </>
    );
}
